import i18next from 'i18next'
import * as React from 'react'
import { ButtonHTMLAttributes } from 'react'
import { routeUpdaters } from '../site/root/routes/routesState'
import { DataLayer } from './datalayer'
import { ExternalLink } from './externalLink'
import { SVGIconType, SVGIcon } from './svgicon'

// ----- Shared

export type ClickableButtonProps = {
  kind: 'button'
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  type?: 'button' | 'submit'
  htmlAttributes?: ButtonHTMLAttributes<HTMLButtonElement>
}

export type ClickableLinkProps = {
  kind: 'link'
  to: string
  target?: string
  onClick?: () => void
  htmlAttributes?: any // Couldn't find the right attributes type
}

// ----- Default button

type ButtonVariantProps = {
  variant: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'transparent'
}

type ButtonSizeProps = 's' | 'm'

export type ClickableIconProps = {
  iconId?: SVGIconType
  iconSize?: string
}

export type ButtonBaseProps = {
  size: ButtonSizeProps
  label: string | JSX.Element
  title?: string
  isDisabled?: boolean
  extraClasses?: string
} & ClickableIconProps &
  (ClickableButtonProps | ClickableLinkProps)

export const ButtonLayout = (p: ButtonBaseProps & ButtonVariantProps) => {
  const ButtonClasses = `button--${p.variant} button--${p.size} ${
    p.extraClasses || ''
  }`
  const ButtonIcon =
    p.iconId && p.iconSize ? (
      <SVGIcon className="button__icon" size={p.iconSize} iconId={p.iconId} />
    ) : (
      <></>
    )
  return p.kind === 'button' ? (
    <button
      className={ButtonClasses}
      disabled={p.isDisabled || false}
      title={p.title || ''}
      onClick={p.onClick}
      type={p.type || 'button'}
      {...p.htmlAttributes}
    >
      <span className="button__label">{p.label}</span>
      {ButtonIcon}
    </button>
  ) : (
    <ExternalLink
      to={p.to}
      target={p.target}
      className={`${ButtonClasses} ${p.isDisabled ? ' disabled' : ''}`}
      title={p.title || ''}
      onClick={p.onClick || undefined}
      {...p.htmlAttributes}
    >
      <span className="button__label">{p.label}</span>
      {ButtonIcon}
    </ExternalLink>
  )
}

export const ButtonPrimary = (p: ButtonBaseProps) => (
  <ButtonLayout variant="primary" {...p} />
)

export const ButtonSecondary = (p: ButtonBaseProps) => (
  <ButtonLayout variant="secondary" {...p} />
)

export const ButtonTertiary = (p: ButtonBaseProps) => (
  <ButtonLayout variant="tertiary" {...p} />
)

export const ButtonGhost = (p: ButtonBaseProps) => (
  <ButtonLayout variant="ghost" {...p} />
)

export const ButtonTransparent = (p: ButtonBaseProps) => (
  <ButtonLayout variant="transparent" {...p} />
)

// ----- Tab Button (search results)

type TabButtonProps = {
  to: string
  label: string
  isActive: boolean
  htmlAttributes?: ButtonHTMLAttributes<HTMLButtonElement>
  onClick: () => void
}

export const TabButton = (p: TabButtonProps) =>
  p.isActive ? (
    <ButtonSecondary
      kind="link"
      to={p.to}
      size="s"
      label={p.label}
      extraClasses="tabs__button"
      htmlAttributes={{
        role: 'tab',
        ...p.htmlAttributes,
      }}
      onClick={p.onClick}
    />
  ) : (
    <ButtonGhost
      kind="link"
      to={p.to}
      size="s"
      label={p.label}
      extraClasses="tabs__button"
      htmlAttributes={{
        role: 'tab',
        ...p.htmlAttributes,
      }}
      onClick={p.onClick}
    />
  )

// ----- Icon button

type IconButtonBaseProps = Partial<{
  srText: string
  iconId: SVGIconType
  iconSize: string
  extraClasses?: string
}> &
  (ClickableButtonProps | ClickableLinkProps)

export type IconButtonProps = {
  variant: 'social' | 'close'
} & IconButtonBaseProps

export const IconButtonLayout = (p: IconButtonProps) => {
  const ButtonClasses = `button--icon icon--${p.variant} ${
    p.extraClasses || ''
  }`
  return p.kind === 'button' ? (
    <button
      className={ButtonClasses}
      title={p.srText}
      onClick={p.onClick}
      type={p.type || undefined}
    >
      <span className="sr-text">{p.srText}</span>
      {p.iconId && (
        <SVGIcon
          className="button__icon"
          size={p.iconSize || ''}
          iconId={p.iconId}
        />
      )}
    </button>
  ) : (
    <a
      href={p.to}
      className={ButtonClasses}
      title={p.srText}
      onClick={p.onClick || undefined}
      target={p.target || undefined}
    >
      <span className="sr-text">{p.srText}</span>
      {p.iconId && (
        <SVGIcon
          className="button__icon"
          size={p.iconSize || ''}
          iconId={p.iconId}
        />
      )}
    </a>
  )
}

export const IconButtonSocial = (p: IconButtonBaseProps) => (
  <IconButtonLayout variant="social" {...p} />
)

export const IconButtonClose = (p: IconButtonBaseProps) => (
  <IconButtonLayout
    variant="close"
    iconId="close"
    iconSize="18"
    srText={i18next.t('common:close')}
    {...p}
  />
)

export const StartTrialButton = (p: {
  size?: ButtonSizeProps
  extraClasses?: string
}) => (
  <ButtonPrimary
    kind="link"
    to={
      routeUpdaters(i18next.language).register.url +
      '?originPath=' +
      window.location.pathname
    }
    size={p.size || 'm'}
    label={i18next.t('subscription:startYourFreeTrial')}
    extraClasses={p.extraClasses}
    onClick={() =>
      DataLayer.headerButtonsClick(i18next.t('subscription:startYourFreeTrial'))
    }
  />
)

export const CreateCollectionButton = (p: { onClick: () => void }) => (
  <ButtonPrimary
    kind="button"
    onClick={p.onClick}
    size="s"
    label={i18next.t('collection:createNewCollection')}
    iconId="hexagon-sliced-open-bold"
    iconSize="24"
  />
)
